import { useMemo } from 'react';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  getTypeOrNull,
  useSurfacesExclusivityGroupsQuery,
} from '@spotify/confidence-graphql';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityLabelItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';

import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface SurfaceItem {
  name: string;
  displayName?: string;
}

const getSurfaces = (value: unknown): SurfaceItem[] => {
  if (!value) {
    return [];
  }

  // Handle single string (e.g., "surfaces/...")
  if (typeof value === 'string') {
    return [{ name: value }];
  }

  // Handle array
  if (!Array.isArray(value)) {
    return [];
  }

  const surfaces: SurfaceItem[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      surfaces.push({ name: item });
    } else if (
      typeof item === 'object' &&
      item !== null &&
      'name' in item &&
      typeof item.name === 'string'
    ) {
      const displayName =
        'displayName' in item && typeof item.displayName === 'string'
          ? item.displayName
          : undefined;
      surfaces.push({ name: item.name, displayName });
    }
  }

  return surfaces;
};

const surfacePrefix = (count: number) => {
  return count === 1 ? 'surface' : 'surfaces';
};

const SurfacesChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldSurfaces = getSurfaces(change.oldValue);
  const newSurfaces = getSurfaces(change.newValue);

  const added = newSurfaces.filter(
    n => !oldSurfaces.find(o => o.name === n.name),
  );
  const removed = oldSurfaces.filter(
    o => !newSurfaces.find(n => n.name === o.name),
  );

  const filterString = [
    ...new Set([...added.map(s => s.name), ...removed.map(s => s.name)]),
  ]
    .map(name => `name:"${name}"`)
    .join(' OR ');

  const { data } = useSurfacesExclusivityGroupsQuery({
    variables: {
      filter: filterString,
      showDeletedHoldbacks: false,
      showDeletedExclusivityGroups: false,
    },
    skip: !filterString,
    fetchPolicy: 'cache-first',
  });

  const displayNameMap = useMemo(() => {
    const liveSurfaces = getTypeOrNull(data?.surfaces)?.surfaces ?? [];
    return new Map(liveSurfaces.map(s => [s.name, s.displayName]));
  }, [data]);

  const getDisplayName = (surface: SurfaceItem): string => {
    return (
      displayNameMap.get(surface.name) ??
      surface.displayName ??
      extractLastNameComponent(surface.name) ??
      surface.name
    );
  };

  // For single-item changes, show inline with title
  if (added.length === 1 && removed.length === 0) {
    return (
      <ActivityLabelItem
        text="Added surface"
        tagLabel={getDisplayName(added[0])}
      />
    );
  }

  if (removed.length === 1 && added.length === 0) {
    return (
      <ActivityLabelItem
        text="Removed surface"
        tagLabel={getDisplayName(removed[0])}
      />
    );
  }

  // For multiple changes, show sections
  return (
    <>
      {added.length > 0 && (
        <ActivityItem.Section label="Added surfaces">
          {added.map(surface => (
            <ActivityTagItem
              key={surface.name}
              tagLabel={getDisplayName(surface)}
              newValue=""
            />
          ))}
        </ActivityItem.Section>
      )}

      {removed.length > 0 && (
        <ActivityItem.Section label="Removed surfaces">
          {removed.map(surface => (
            <ActivityTagItem
              key={surface.name}
              tagLabel={getDisplayName(surface)}
              oldValue=""
            />
          ))}
        </ActivityItem.Section>
      )}
    </>
  );
};

export const SURFACES_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'surfaces-change',
  predicate: ({ change }) => {
    if (change.fieldPath !== '/surfaces') {
      return false;
    }

    const oldSurfaces = getSurfaces(change.oldValue);
    const newSurfaces = getSurfaces(change.newValue);

    const added = newSurfaces.filter(
      n => !oldSurfaces.find(o => o.name === n.name),
    );
    const removed = oldSurfaces.filter(
      o => !newSurfaces.find(n => n.name === o.name),
    );

    return added.length > 0 || removed.length > 0;
  },
  title: change => {
    const oldSurfaces = getSurfaces(change.oldValue);
    const newSurfaces = getSurfaces(change.newValue);

    const addedCount = newSurfaces.filter(
      n => !oldSurfaces.find(o => o.name === n.name),
    ).length;
    const removedCount = oldSurfaces.filter(
      o => !newSurfaces.find(n => n.name === o.name),
    ).length;

    if (addedCount > 0 && removedCount === 0) {
      return `Added ${addedCount} ${surfacePrefix(addedCount)}`;
    }

    if (removedCount > 0 && addedCount === 0) {
      return `Removed ${removedCount} ${surfacePrefix(removedCount)}`;
    }

    if (addedCount > 0 && removedCount > 0) {
      return 'Updated surfaces';
    }

    return 'Updated surfaces';
  },
  render: SurfacesChangeRender,
});
